import { useState, useMemo } from "react";
import {
  Business,
  Service,
  ServiceVariant,
  BookingService,
} from "@/types/booking";
import { Button } from "../ui/button";
import { Clock, Tag } from "lucide-react";
import {
  formatDuration,
  formatPrice,
  isPromotionActive,
  calculateDiscountedPrice,
  getDiscountAmount,
  formatPromotionEndDate,
  getBusinessCurrency,
} from "@/lib/utils";
import ServiceVariantDialog from "./ServiceVariantDialog";

const ServiceTile = ({
  service,
  businessInfo,
  selected,
  onClick,
  onSelectVariant,
  allowMultiple = false,
  selectedServices = [],
  highlighted = false,
}: {
  service: Service;
  businessInfo: Business | null;
  selected: boolean;
  onClick: () => void;
  onSelectVariant?: (serviceId: string, variant: ServiceVariant | null) => void;
  allowMultiple?: boolean;
  selectedServices?: BookingService[];
  highlighted?: boolean;
}) => {
  const [isVariantDialogOpen, setIsVariantDialogOpen] = useState(false);
  const currency = getBusinessCurrency(businessInfo);

  // Calculate price and duration ranges for services with variants
  const priceRange = useMemo(() => {
    if (
      service.hasVariants &&
      service.serviceVariants &&
      service.serviceVariants.length > 0
    ) {
      const allPrices = [
        service.price,
        ...service.serviceVariants.map((variant) => variant.price),
      ];
      const minPrice = Math.min(...allPrices);
      const maxPrice = Math.max(...allPrices);
      return { min: minPrice, max: maxPrice, hasRange: minPrice !== maxPrice };
    }
    return { min: service.price, max: service.price, hasRange: false };
  }, [service.price, service.serviceVariants, service.hasVariants]);

  const durationRange = useMemo(() => {
    if (
      service.hasVariants &&
      service.serviceVariants &&
      service.serviceVariants.length > 0
    ) {
      const allDurations = [
        service.duration,
        ...service.serviceVariants.map((variant) => variant.duration),
      ];
      const minDuration = Math.min(...allDurations);
      const maxDuration = Math.max(...allDurations);
      return {
        min: minDuration,
        max: maxDuration,
        hasRange: minDuration !== maxDuration,
      };
    }
    return { min: service.duration, max: service.duration, hasRange: false };
  }, [service.duration, service.serviceVariants, service.hasVariants]);

  // Calculate count of selected items for this service
  const selectedCount = useMemo(() => {
    if (!selectedServices || selectedServices.length === 0) return 0;

    return selectedServices.filter((selectedService) => {
      // Count parent service
      if (selectedService.id === service.id) return true;
      // Count variants of this service
      if (selectedService.id.startsWith(`${service.id}-`)) return true;
      return false;
    }).length;
  }, [selectedServices, service.id]);

  const handleServiceClick = () => {
    // Check if service has variants
    if (
      service.hasVariants &&
      service.serviceVariants &&
      service.serviceVariants.length > 0
    ) {
      setIsVariantDialogOpen(true);
    } else {
      onClick();
    }
  };

  const handleVariantSelection = (
    serviceId: string,
    variant: ServiceVariant | null
  ) => {
    if (onSelectVariant) {
      onSelectVariant(serviceId, variant);
    }
    setIsVariantDialogOpen(false);
  };
  return (
    <div
      key={service.id}
      className={`p-6 rounded-lg ${highlighted ? "service-deep-link-glow" : ""}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-base font-semibold text-grey-900 mb-1 font-heading">
            {service.name}
          </h3>
          {service.description && (
            <p className="text-grey-600 text-sm leading-relaxed mb-3">
              {service.description}
            </p>
          )}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-grey-400" />
              <span className="text-grey-600 font-medium">
                {formatDuration(durationRange.min)}
                {durationRange.hasRange && (
                  <span className="text-grey-500 ml-1">
                    - {formatDuration(durationRange.max)}
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right ml-4">
          {service.hasVariants &&
          service.serviceVariants &&
          service.serviceVariants.length > 0 ? (
            // Show price range for services with variants
            <div>
              <p className="text-xs font-medium text-grey-600 uppercase tracking-wide">
                From
              </p>
              <p className="text-lg font-semibold text-green-300">
                {formatPrice(priceRange.min, currency)}
              </p>
            </div>
          ) : service.pricingType === "starting_at" ? (
            <div>
              <p className="text-xs font-medium text-grey-600 uppercase tracking-wide">
                From
              </p>
              <p className="text-lg font-semibold text-green-300">
                {formatPrice(service.price, currency)}
              </p>
            </div>
          ) : (
            <p className="text-lg font-semibold text-green-300">
              {formatPrice(service.price, currency)}
            </p>
          )}
        </div>
      </div>
      <Button
        hidden={!businessInfo?.acceptsOnlineBookings}
        onClick={handleServiceClick}
        className={
          selectedCount > 0
            ? "w-full bg-green-300 text-white"
            : "w-full border-green-300 text-green-300 hover:bg-green-300 hover:text-white transition-colors"
        }
        variant={selectedCount > 0 ? "default" : "outline"}
      >
        {selectedCount > 0 ? `${selectedCount} Selected` : "Book This Service"}
      </Button>

      <ServiceVariantDialog
        service={service}
        businessInfo={businessInfo}
        isOpen={isVariantDialogOpen}
        onClose={() => setIsVariantDialogOpen(false)}
        onSelectVariant={handleVariantSelection}
        allowMultiple={allowMultiple}
        selectedServices={selectedServices}
      />
    </div>
  );
};

export default ServiceTile;
